import React from 'react';
import { Container, Typography, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './PrivacyPolicySection.css';

const sections = [
  {
    title: 'Interpretation and Definitions',
    content: (
      <>
        <Typography gutterBottom>
          The words with capitalized initial letters have meanings defined under the following conditions. The following definitions apply regardless of whether they appear in singular or plural.
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Definitions</Typography>
        <ul>
          <li><b>Account:</b> A unique account created for you to access our services or parts of our services.</li>
          <li><b>Company:</b> Refers to Virves Software Solutions ("the Company", "We", "Us", or "Our").</li>
          <li><b>Cookies:</b> Small files placed on your device by a website, containing details of your browsing history and other uses.</li>
          <li><b>Country:</b> Refers to: Karnataka, India.</li>
          <li><b>Device:</b> Any device that can access the service, such as a computer, cellphone, or tablet.</li>
          <li><b>Personal Data:</b> Any information that relates to an identified or identifiable individual.</li>
          <li><b>Service:</b> Refers to the Virves Software Solutions website and related offerings.</li>
          <li><b>Service Provider:</b> Any third-party company or individual employed by us to facilitate or provide the service, or to assist us in analyzing how the service is used.</li>
          <li><b>Usage Data:</b> Data collected automatically, either generated by the use of the service or from the service infrastructure itself (e.g., duration of a page visit).</li>
          <li><b>You:</b> The individual accessing or using the service, or the company or other legal entity on behalf of which such individual is accessing or using the service.</li>
        </ul>
      </>
    )
  },
  {
    title: 'Collecting and Using Your Personal Data',
    content: (
      <>
        <Typography gutterBottom>
          We may ask you to provide certain personally identifiable information that can be used to contact or identify you. This may include your email address, phone number, and usage data.
        </Typography>
        <Typography gutterBottom>
          Usage Data is collected automatically when using our service. This may include information such as your device’s IP address, browser type, browser version, the pages you visit, the time and date of your visit, time spent on those pages, and other diagnostic data. When you access the service by or through a mobile device, we may collect information such as device type, unique device ID, IP address, operating system, browser type, and other diagnostic data.
        </Typography>
      </>
    )
  },
  {
    title: 'Tracking Technologies and Cookies',
    content: (
      <>
        <Typography gutterBottom>
          We use cookies and similar tracking technologies to track activity on our service and store certain information. These technologies help us improve and analyze our service.
        </Typography>
        <ul>
          <li><b>Cookies or Browser Cookies:</b> Small files placed on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some parts of our service may not function properly.</li>
          <li><b>Web Beacons:</b> Small electronic files that allow us to count users who have visited certain pages or opened an email, and for other related website statistics.</li>
        </ul>
        <Typography gutterBottom>
          We use both session and persistent cookies for purposes such as authentication, remembering your preferences, and improving your experience. For more information about cookies and your choices, please see our Cookies Policy.
        </Typography>
      </>
    )
  },
  {
    title: 'Use of Your Personal Data',
    content: (
      <>
        <Typography gutterBottom>
          Virves Software Solutions may use your personal data for the following purposes:
        </Typography>
        <ul>
          <li>To provide and maintain our service, including monitoring usage.</li>
          <li>To manage your account and registration as a user.</li>
          <li>To contact you regarding updates, offers, or information related to our services.</li>
          <li>To manage your requests and provide customer support.</li>
          <li>To improve our service, products, and user experience through data analysis and feedback.</li>
        </ul>
        <Typography gutterBottom>
          We may share your personal information with service providers, business partners, or affiliates as needed to provide our services, comply with legal obligations, or with your consent.
        </Typography>
      </>
    )
  },
  {
    title: 'Disclosure of Your Personal Data',
    content: (
      <>
        <Typography gutterBottom>
          We may disclose your personal data in the following situations:
        </Typography>
        <ul>
          <li>To comply with legal obligations or respond to lawful requests by public authorities.</li>
          <li>To protect and defend the rights or property of Virves Software Solutions.</li>
          <li>To prevent or investigate possible wrongdoing in connection with the service.</li>
          <li>To protect the personal safety of users or the public.</li>
          <li>In connection with a business transfer, such as a merger or acquisition.</li>
          <li>With your consent, for any other purpose disclosed at the time of collection.</li>
        </ul>
        <Typography gutterBottom>
          We take reasonable steps to protect your personal data, but no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
        </Typography>
      </>
    )
  }
];

const PrivacyPolicySection = () => (
  <Box className="privacy-section-bg">
    <Container maxWidth="md">
      <Typography variant="h2" className="privacy-section-title" gutterBottom>
        Privacy Policy
      </Typography>
      {sections.map((section, idx) => (
        <Accordion key={idx} className="privacy-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="privacy-accordion-summary">
            <Typography>{section.title}</Typography>
          </AccordionSummary>
          <AccordionDetails className="privacy-accordion-details">
            {section.content}
          </AccordionDetails>
        </Accordion>
      ))}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#112D4E', mb: 1 }}>
          Contact Us
        </Typography>
        <Typography>
          If you have any questions about this Privacy Policy, you can contact us at <a href="mailto:info@virves.com">info@virves.com</a>.
        </Typography>
      </Box>
    </Container>
  </Box>
);

export default PrivacyPolicySection; 